// One-way playback using the same server-owned model/voice as live Karen.
// No microphone, app tools, browser voice fallback, or synthetic completion.
export function speakKarenNarration(text, { signal } = {}) {
  if (!text?.trim()) return Promise.resolve(true);
  if (signal?.aborted || typeof RTCPeerConnection === "undefined" || typeof Audio === "undefined") return Promise.resolve(false);
  return new Promise(resolve => {
    let peer, channel, audio, timer;
    let finished = false;
    let playbackStarted = false;
    let responseSucceeded = false;
    let playbackStopped = false;
    const control = new AbortController();
    const finish = success => {
      if (finished) return;
      finished = true;
      clearTimeout(timer);
      signal?.removeEventListener("abort", cancel);
      control.abort();
      if (audio) { audio.pause(); audio.srcObject = null; }
      channel?.close(); peer?.close();
      resolve(success);
    };
    const cancel = () => finish(false);
    const complete = () => {
      if (playbackStarted && playbackStopped && responseSucceeded) finish(true);
    };
    signal?.addEventListener("abort", cancel, { once: true });
    // Bound connection setup only. Never cut a speaking Karen off on a timer.
    timer = setTimeout(() => finish(false), 45000);
    (async () => {
      try {
        peer = new RTCPeerConnection();
        audio = new Audio(); audio.autoplay = true;
        peer.addTransceiver("audio", { direction: "recvonly" });
        peer.ontrack = event => {
          if (finished) return;
          audio.srcObject = event.streams[0];
          audio.play().catch(() => finish(false));
        };
        peer.onconnectionstatechange = () => {
          if (["failed", "closed", "disconnected"].includes(peer.connectionState)) finish(false);
        };
        channel = peer.createDataChannel("oai-events");
        channel.onopen = () => {
          if (finished) return;
          channel.send(JSON.stringify({ type: "response.create", response: {
            output_modalities: ["audio"], tool_choice: "none",
            instructions: "Read this announcement exactly once, without additions: " + JSON.stringify(text),
          } }));
        };
        channel.onmessage = event => {
          if (finished) return;
          let value;
          try { value = JSON.parse(event.data); } catch { finish(false); return; }
          if (value.type === "error" || value.type === "output_audio_buffer.cleared") finish(false);
          if (value.type === "output_audio_buffer.started") {
            playbackStarted = true; clearTimeout(timer);
          }
          if (value.type === "response.done") {
            if (value.response?.status !== "completed") { finish(false); return; }
            responseSucceeded = true; complete();
          }
          if (value.type === "output_audio_buffer.stopped") {
            playbackStopped = true; complete();
          }
        };
        channel.onerror = () => finish(false);
        channel.onclose = () => finish(false);
        const offer = await peer.createOffer();
        if (finished) return;
        await peer.setLocalDescription(offer);
        const response = await fetch(`${process.env.REACT_APP_BACKEND_URL || ""}/api/universal_intent/realtime/session?narration=1`, {
          method: "POST", credentials: "include", signal: control.signal,
          headers: { "Content-Type": "application/sdp" }, body: offer.sdp,
        });
        if (!response.ok) throw new Error("Karen voice unavailable");
        const sdp = await response.text();
        if (!finished) await peer.setRemoteDescription({ type: "answer", sdp });
      } catch { finish(false); }
    })();
  });
}
